import { V3Models } from "@istari/istari-client";
import { useCallback } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { useSetConversationTranscript } from "~/ai-chat/data-source/use-conversations";
import { applyChatSaveFollowUps } from "~/ai-chat/feature/apply-chat-save-follow-ups";
import { useAiChat } from "~/ai-chat/feature/use-ai-chat";
import type { ChatTranscriptItem } from "~/ai-chat/model/chat-message";
import {
  type ChatSaveChoices,
  type ChatSaveDestination,
  chatSaveWarnings,
  describeSaveDestination,
} from "~/ai-chat/model/chat-save";
import { chatResourceFilename, chatTranscriptToMarkdown } from "~/ai-chat/model/chat-transcript-markdown";
import { usePatchModelControlTags } from "~/api/useControlTags";
import { useAssignInfosecLevel } from "~/api/useInfosecLevels";
import { useCommitFileToBranch } from "~/branching/feature/use-commit-file-to-branch";
import { useCreateResource } from "~/resource/data-source/use-create-resource";
import { buildResourcePath } from "~/routes";

/**
 * Save the current chat transcript as a Markdown resource, apply the chosen classification + control tags,
 * optionally add it to the branch the chat is open on, and link it to the conversation (so a later share
 * reopens that resource instead of saving a duplicate). Returns the created resource id, or null on failure.
 */
export function useSaveChatResource(destination?: ChatSaveDestination) {
  const createResource = useCreateResource();
  const assignInfosecLevel = useAssignInfosecLevel();
  // The success toast already names a tag miss; the hook's own toast would contradict it and,
  // from a popped-out chat, land in the main window.
  const patchControlTags = usePatchModelControlTags({ onError: () => undefined });
  const commitFileToBranch = useCommitFileToBranch(destination?.systemId);
  const setTranscript = useSetConversationTranscript();
  const { activeConversationId, setSavedTranscriptResourceId } = useAiChat();
  const navigate = useNavigate();

  const save = useCallback(
    async (title: string, items: ChatTranscriptItem[], choices: ChatSaveChoices = {}): Promise<string | null> => {
      const displayName = title.trim() || "Istari chat";
      const markdown = chatTranscriptToMarkdown(displayName, items);
      const file = new File([markdown], chatResourceFilename(displayName), { type: "text/markdown" });

      try {
        // Model, not File: the Files explorer lists only Model/Artifact resources,
        // so a File-typed saved chat would be invisible in the library.
        const resource = await createResource.mutateAsync({
          file,
          displayName,
          resourceType: V3Models.ResourceTypeDto.Model,
        });

        const outcome = await applyChatSaveFollowUps(
          { resourceId: resource.resourceId, fileRevisionId: resource.fileRevisionId, displayName },
          choices,
          destination,
          {
            assignInfosecLevel: (id, infosecLevelId) =>
              assignInfosecLevel.mutateAsync({ type: "model", id, infosecLevelId }),
            assignControlTags: (modelId, ids) =>
              patchControlTags.mutateAsync({ modelId, controlTagIdsActionTypes: [{ ids, action: "set" }] }),
            addToBranch: (fileRevisionId, name, target) =>
              commitFileToBranch.mutateAsync({
                branchId: target.branch.id,
                expectedHeadSnapshotId: target.branch.headCommitId,
                fileRevisionId,
                commitMessage: `Add ${name}`,
              }),
          },
        );

        // Remember + link the resource so a later Share reopens it instead of saving a duplicate. Session
        // memory is reliable immediately; the backend link survives a reload. The link is best-effort: on
        // failure the save still succeeds (the mutation reports the error) and we warn that re-sharing after
        // a reload may save a new copy (the session memory is gone by then).
        setSavedTranscriptResourceId(resource.resourceId);
        const linkWarnings: string[] = [];
        if (activeConversationId) {
          try {
            await setTranscript.mutateAsync({
              conversationId: activeConversationId,
              transcriptResourceId: resource.resourceId,
            });
          } catch {
            linkWarnings.push("The chat couldn't be linked, so re-sharing it later may save a new copy.");
          }
        }

        const savedTo =
          outcome.placedInSystem && destination
            ? `Chat saved to ${describeSaveDestination(destination)}.`
            : "Chat saved to your library.";
        const openResource = () => navigate(buildResourcePath({ resourceId: resource.resourceId }));
        toast.success([savedTo, ...chatSaveWarnings(outcome, destination?.systemName), ...linkWarnings].join(" "), {
          action: { label: "Open", onClick: openResource },
        });
        return resource.resourceId;
      } catch {
        toast.error("Couldn't save the chat. Please try again.");
        return null;
      }
    },
    [
      createResource,
      assignInfosecLevel,
      patchControlTags,
      commitFileToBranch,
      setTranscript,
      activeConversationId,
      setSavedTranscriptResourceId,
      destination,
      navigate,
    ],
  );

  return {
    save,
    // Includes the conversation link: without it, isSaving flips false between the resource create and the
    // awaited link, and a second click in that gap would create a duplicate resource.
    isSaving:
      createResource.isPending ||
      assignInfosecLevel.isPending ||
      patchControlTags.isPending ||
      commitFileToBranch.isPending ||
      setTranscript.isPending,
  };
}
